import {
  type DispatchAddress,
  type JobLeg,
  type JobPayload,
  type JobPayloadData,
  toWireAddress,
} from '../../../api/dispatch';
import {
  type BookingFormState,
  type LegState,
  parseCount,
  parseLuggageCount,
  pickupAirportCode,
} from './bookingForm';

/**
 * Form state → the exact wire payload the dashboard's JobForm.toPayload()
 * produces. Every oddity here is dispatch-be contract, not preference — the
 * dashboard source (job_form.dart) is the reference for all of it.
 */

/**
 * Interpret an agent-entered wall time (`YYYY-MM-DDTHH:mm`) in the pickup's
 * IANA zone and emit the UTC ISO string dispatch expects (`...Z`). Two-pass
 * Intl offset trick: guess the instant assuming zero offset, measure what wall
 * time that instant shows in the zone, correct — the second pass converges
 * across DST boundaries. Null zone falls back to the browser's zone.
 */
export function wallTimeToUtcIso(localWall: string, ianaZone: string | null): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(localWall);
  if (!m) return null;
  const [y, mo, d, h, mi] = [m[1], m[2], m[3], m[4], m[5]].map(Number);
  if (!ianaZone) return new Date(y, mo - 1, d, h, mi).toISOString();
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi);
  const zonedWall = (instant: number) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date(instant));
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    // Some engines format midnight as hour 24.
    const hour = get('hour') === 24 ? 0 : get('hour');
    return Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'));
  };
  let instant = wallAsUtc;
  instant -= zonedWall(instant) - wallAsUtc;
  instant -= zonedWall(instant) - wallAsUtc;
  return new Date(instant).toISOString();
}

/**
 * Unresolved legs still ship their raw text in addressLine1 (zeros elsewhere):
 * a partial DRAFT must not lose what the AI extracted just because nobody
 * resolved it yet. Full job saves are gated on `resolved` by validateForSave.
 */
const wireAddress = (leg: LegState): DispatchAddress =>
  leg.resolved
    ? toWireAddress(leg.resolved)
    : {
        addressLine1: leg.text.trim(),
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        place_id: '',
        latitude: 0,
        longitude: 0,
        addressType: 'N',
        zoneId: null,
      };

type PaymentBlock = Pick<JobPayload, 'paymentMethod' | 'paymentMethodId' | 'isBilling'>;

/**
 * The three shapes dispatch's payment validation accepts
 * (`commonApis/jobcreate.js`): bill later, a card with its id, or a payment
 * link. Anything else is a 400 on a request that must not be retried blindly.
 *
 * Payment is a JOB concern only. Dispatch nulls the pair for a quote anyway
 * (`isBilling || isQuote`), and a choice sent on one would still reach the
 * customer through the `create_quote` mail's payment-method line — so a quote
 * ships with no payment at all, exactly as it did before there was a chooser.
 */
const paymentBlock = (form: BookingFormState, isQuote: boolean): PaymentBlock => {
  if (isQuote) return { paymentMethod: null, paymentMethodId: null, isBilling: false };
  if (form.paymentMode === 'billLater') {
    return { paymentMethod: null, paymentMethodId: null, isBilling: true };
  }
  if (form.paymentMode === 'card') {
    return {
      paymentMethod: 'card',
      paymentMethodId: form.paymentMethodId.trim() || null,
      isBilling: false,
    };
  }
  return { paymentMethod: 'paymentLink', paymentMethodId: null, isBilling: false };
};

/**
 * The `booked*` trio: who placed the job, as dispatch stores it on the job row
 * and prints on the confirmation.
 *
 * A named account answers for itself — an affiliate by its company (that is the
 * desk, not whoever happened to type the mail), a customer by its own name — so
 * the guest fields are only consulted when nobody is named. That ordering is
 * what keeps an agent's edit to a guest booker meaningful while making an
 * affiliate booking say the affiliate.
 */
const bookedIdentity = (
  form: BookingFormState,
  affiliate: BookingFormState['bookerAffiliate'],
  customer: BookingFormState['bookerCustomer'],
) => {
  const account = affiliate
    ? {
        name: affiliate.companyName ?? '',
        email: affiliate.email ?? affiliate.primaryContactEmail ?? '',
        phone: affiliate.phone ?? affiliate.primaryContactPhone ?? '',
      }
    : customer
      ? { name: customer.name ?? '', email: customer.email ?? '', phone: customer.phone ?? '' }
      : { name: form.bookerName, email: form.bookerEmail, phone: form.bookerPhone };
  return {
    bookedName: account.name.trim(),
    bookedEmail: account.email.trim().toLowerCase(),
    bookedPhone: account.phone.trim(),
  };
};

export function buildJobPayload(
  form: BookingFormState,
  options: { isQuote: boolean },
): JobPayload {
  const pickupIso = form.pickupDatetime
    ? wallTimeToUtcIso(form.pickupDatetime, form.timeZoneId)
    : null;

  const leg = (
    source: LegState,
    kind: 'P' | 'S' | 'D',
    sequence: number,
  ): JobLeg => ({
    address: wireAddress(source),
    otherData: {
      addressId: null,
      type: kind,
      // '' always — the 'create' sentinel demands a flightData block we don't
      // have; the human-readable flight number rides in flightIata.
      flightNumber: '',
      // Flight details attach to the pickup leg — the arrival-pickup case,
      // which is the only flight shape this form captures. The airport code is
      // what narrows dispatch's flight-number lookup to the leg landing here.
      airPortCode: kind === 'P' ? pickupAirportCode(form.pickup) : '',
      isFbo: false,
      flightIata: kind === 'P' ? form.flightNumber.trim() : '',
      airlineName: kind === 'P' ? form.airline.trim() : '',
      pickUpTimeDate: kind === 'P' ? pickupIso : null,
      waitTime: null,
      timeIn: kind === 'P' ? pickupIso : null,
      arriveAirportAt: 'At pickup time',
      sequence,
    },
  });

  const hourly = form.serviceType === 'hourly';
  // Hourly "same as pickup" re-serializes the pickup address as the dropoff
  // leg — the dashboard's job_form.dart:256 rule, not an optimization.
  const dropoffSource = hourly && form.dropoffSameAsPickup ? form.pickup : form.dropoff;
  const stops = form.stops.filter((s) => s.text.trim() || s.resolved);

  const passengerName = `${form.passengerFirstName.trim()} ${form.passengerLastName.trim()}`.trim();
  const passengerEmpty =
    !passengerName && !form.passengerPhone.trim() && !form.passengerEmail.trim();

  // One account id, two keys, and which one it lands in is the booker's type —
  // `job_form.dart:235`. Sending it as both would make dispatch bill twice over.
  const affiliateBooker = form.bookerType === 'affiliate' ? form.bookerAffiliate : null;
  const customerBooker = form.bookerType === 'customer' ? form.bookerCustomer : null;

  const data: JobPayloadData = {
    isSendNotification: form.sendNotifications,
    affiliateId: affiliateBooker?.id ?? null,
    customerId: customerBooker?.id ?? null,
    affiliateValue: 0,
    vehicleTypeId: form.vehicleTypeId,
    type: hourly ? 'H' : 'O',
    duration: hourly ? Number(form.durationHours) || null : null,
    passengerCount: parseCount(form.passengerCount),
    luggageCount: parseLuggageCount(form.luggageCount),
    // Both note rows always ship, ids are STRINGS — '1' customer, '3' chauffeur.
    notes: [
      { id: '1', value: form.customerNote.trim() },
      { id: '3', value: form.chauffeurNote.trim() },
    ],
    source: 'A',
    // The BOOKER, which is only the passenger when the two really are the same
    // person. These used to be copied from the passenger fields, which put
    // "Carlo Lombardo" on the wire as the booker of a trip his affiliate placed.
    ...bookedIdentity(form, affiliateBooker, customerBooker),
    billingName: '',
    billingCompanyName: '',
    voucher: '',
    // Omitted (not null) when unknown — the dashboard drops the key too.
    ...(form.timeZoneId ? { timeZone: form.timeZoneId } : {}),
    // Affiliate bookings only, and omitted rather than empty otherwise —
    // `job_form.dart:253` spreads the key in under the same condition.
    ...(affiliateBooker && form.bookerReference.trim()
      ? { requesterResNo: form.bookerReference.trim() }
      : {}),
  };

  return {
    isQuote: options.isQuote,
    ...paymentBlock(form, options.isQuote),
    uid: '',
    data,
    pickUp: leg(form.pickup, 'P', 0),
    dropOff: leg(dropoffSource, 'D', 100),
    stopOff: stops.map((s, index) => leg(s, 'S', index + 1)),
    // An empty passenger is the LITERAL {} on the wire, never null/omitted.
    primaryPassenger: passengerEmpty
      ? {}
      : {
          id: 0,
          name: passengerName,
          email: form.passengerEmail.trim().toLowerCase(),
          phone: form.passengerPhone.trim(),
        },
    secondaryPassenger: {},
    addOns: [],
  };
}
